import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { InstanceProvider } from './contexts/InstanceContext';
import { OfflineProvider } from './contexts/OfflineContext';
import { DataGridProvider } from './contexts/DataGridContext';

// AG Grid Module Registration (required for v35+)
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
ModuleRegistry.registerModules([AllCommunityModule]);

// Import AG Grid styles
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './components/ui/DataGrid.css';
import './components/ui/FilterPanel.css';

// Pages - Folder-based imports
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PDV from './pages/PDV';
import Terminal from './pages/Terminal';
import Products from './pages/Products';
import Categories from './pages/Categories';
import Customers from './pages/Customers';
import Users from './pages/Users';
import Profiles from './pages/Profiles';
import Tables from './pages/Tables';
import Orders from './pages/Orders';
import Cashiers from './pages/Cashiers';
import PaymentMethods from './pages/PaymentMethods';
import Combos from './pages/Combos';
import SalesReport from './pages/Reports/Sales';
import CashierReport from './pages/Reports/Cashier';
import StockReport from './pages/Reports/Stock';
import WebSocketTest from './pages/WebSocketTest';

// Stock management pages
import Batches from './pages/stock/Batches';
import Movements from './pages/stock/Movements';
import PurchaseOrders from './pages/stock/PurchaseOrders';

// New pages: Suppliers, Brands, Units of Measure
import Suppliers from './pages/Suppliers';
import Brands from './pages/Brands';
import UnitsOfMeasure from './pages/UnitsOfMeasure';
import Services from './pages/Services';

// Settings
import Settings from './pages/Settings';
import Customization from './pages/Settings/Customization';

// Pricing Module
import PricingModule from './pages/Pricing';
import CostVariations from './pages/Pricing/CostVariations';

// Printers Configuration
import PrintersPage from './pages/Config/Printers';

// Admin pages
import Instances from './pages/admin/Instances';
import AdminTickets from './pages/admin/Tickets';

// Support Tickets
import Tickets from './pages/Tickets';

// Layout
import MainLayout from './components/layout/MainLayout';

// PWA Install Prompt
import PWAInstallPrompt from './components/ui/PWAInstallPrompt';

import './App.css';

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
      
      {/* Terminal - Standalone page (no MainLayout, no ProtectedRoute - has own auth) */}
      <Route path="/terminal" element={<Terminal />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="pdv" element={<PDV />} />
        <Route path="pdv/:cashierId" element={<PDV />} />
        <Route path="products" element={<Products />} />
        <Route path="categories" element={<Categories />} />
        <Route path="combos" element={<Combos />} />
        <Route path="services" element={<Services />} />
        <Route path="customers" element={<Customers />} />
        <Route path="users" element={<Users />} />
        <Route path="profiles" element={<Profiles />} />
        <Route path="tables" element={<Tables />} />
        <Route path="orders" element={<Orders />} />
        <Route path="cashiers" element={<Cashiers />} />
        <Route path="payment-methods" element={<PaymentMethods />} />
        <Route path="reports/sales" element={<SalesReport />} />
        <Route path="reports/cashier" element={<CashierReport />} />
        <Route path="reports/stock" element={<StockReport />} />
        <Route path="stock/batches" element={<Batches />} />
        <Route path="stock/movements" element={<Movements />} />
        <Route path="stock/purchase-orders" element={<PurchaseOrders />} />
        <Route path="pricing" element={<PricingModule />} />
        <Route path="pricing/variations" element={<CostVariations />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="brands" element={<Brands />} />
        <Route path="units-of-measure" element={<UnitsOfMeasure />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/printers" element={<PrintersPage />} />
        <Route path="settings/customization" element={<Customization />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="admin/instances" element={<Instances />} />
        <Route path="admin/tickets" element={<AdminTickets />} />
        <Route path="websocket-test" element={<WebSocketTest />} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <InstanceProvider>
          <DataGridProvider>
            <OfflineProvider>
              <BrowserRouter>
                <AppRoutes />
                <Toaster position="top-right" richColors />
                <PWAInstallPrompt />
              </BrowserRouter>
            </OfflineProvider>
          </DataGridProvider>
        </InstanceProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
